import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import * as authService from '../services/auth';

const CallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const { setAuthState } = useAppContext();
  const [error, setError] = useState<string | null>(null);
  const processingRef = React.useRef(false);

  useEffect(() => {
    // Prevent multiple simultaneous callback processing
    if (processingRef.current) {
      return;
    }

    const handleOAuthCallback = async () => {
      processingRef.current = true;
      
      try {
        // Get the full callback URL with query parameters
        const callbackUrl = window.location.href;
        
        // Handle the OAuth callback and exchange code for tokens
        await authService.handleCallback(callbackUrl);
        
        // Get the authenticated user and tokens
        const user = authService.getCurrentUser();
        const token = authService.getToken();
        
        if (user && token) {
          // Update app context with authentication state
          setAuthState(user, {
            accessToken: token,
            idToken: '', // ID token not needed after initial auth
            expiresAt: Date.now() + (8 * 60 * 60 * 1000), // 8 hours
          });
          
          // Redirect to home page
          navigate('/');
        } else {
          console.error('[CallbackPage] Missing user or token after exchange');
          setError('Authentication failed: Unable to retrieve user information');
          processingRef.current = false;
        }
      } catch (err) {
        console.error('[CallbackPage] OAuth callback error:', err);
        const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
        
        // Check if this is a "code already used" error
        if (errorMessage.includes('401') || errorMessage.includes('authorization code')) {
          setError('Authentication code expired or already used. Please try logging in again.');
        } else {
          setError(errorMessage);
        }
        processingRef.current = false;
      }
    };

    handleOAuthCallback();
  }, [navigate, setAuthState]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white shadow rounded-lg p-8 max-w-md">
          <div className="text-center">
            <div className="text-red-600 text-xl mb-4">Authentication Error</div>
            <p className="text-gray-700 mb-4">{error}</p>
            <button
              onClick={() => authService.login()}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="bg-white shadow rounded-lg p-8">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-700">Processing authentication...</p>
        </div>
      </div>
    </div>
  );
};

export default CallbackPage;
